import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/page-header";
import { ErrorState } from "@/components/ui/states";
import { Search, HeartHandshake, AtSign, ClipboardCheck, Bell } from "lucide-react";
import { ActivityCard } from "@/components/atividades/ActivityCard";
import { ActivityKanbanBoard } from "@/components/atividades/ActivityKanbanBoard";
import {
  ReconsultaKanbanBoard,
  reconsultasUrgentes,
  type ReconsultaBucket,
} from "@/components/atividades/ReconsultaKanbanBoard";
import { CompletarAtividadeModal } from "@/components/atividades/CompletarAtividadeModal";
import { AtualizarDisponibilidadeDialog } from "@/components/freelancers/AtualizarDisponibilidadeDialog";
import { useReconsultas, useAtualizarReconsultaStatus, type ReconsultaItem } from "@/hooks/useDisponibilidade";
import { useToast } from "@/hooks/use-toast";
import type { ActivityDetail, FazerAmizadeEtapa } from "@/types/activity";

type MainTab = "falar_com_frila" | "mencao" | "revisao_cadastro" | "reconsultas";
type SubTab = "pending" | "completed";

export default function Atividades() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [mainTab, setMainTab] = useState<MainTab>("falar_com_frila");
  const [subTab, setSubTab] = useState<SubTab>("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedActivity, setSelectedActivity] = useState<ActivityDetail | null>(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  // Reconsulta em curso: qual freelancer está no dialog e qual check resolver ao salvar.
  const [reconsultaAtiva, setReconsultaAtiva] = useState<ReconsultaItem | null>(null);

  // Query para "Fazer Amizade" (todas as etapas do kanban)
  const {
    data: fazerAmizadeActivities = [],
    isLoading: isLoadingFazerAmizade,
    isError: isErrorFazerAmizade,
    refetch: refetchFazerAmizade,
  } = useQuery({
    queryKey: ["activities", "falar_com_frila", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_activities_detail")
        .select("*")
        .eq("tipo", "falar_com_frila")
        .eq("assigned_to", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ActivityDetail[];
    },
    enabled: !!user,
  });

  // Query para "Mencoes"
  const {
    data: mencaoActivities = [],
    isLoading: isLoadingMencao,
    isError: isErrorMencao,
    refetch: refetchMencao,
  } = useQuery({
    queryKey: ["activities", "mencao", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_activities_detail")
        .select("*")
        .eq("tipo", "mencao")
        .eq("assigned_to", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ActivityDetail[];
    },
    enabled: !!user,
  });

  // Query para "Revisao de Cadastro"
  const {
    data: revisaoActivities = [],
    isLoading: isLoadingRevisao,
    isError: isErrorRevisao,
    refetch: refetchRevisao,
  } = useQuery({
    queryKey: ["activities", "revisao_cadastro"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_activities_detail")
        .select("*")
        .eq("tipo", "revisao_cadastro")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ActivityDetail[];
    },
    enabled: !!user,
  });

  // Contagem para badges das tabs principais
  const fazerAmizadePendingCount = useMemo(() => {
    return fazerAmizadeActivities.filter(
      (a) => a.etapa_kanban === "pendente" || a.etapa_kanban === "em_contato"
    ).length;
  }, [fazerAmizadeActivities]);

  const mencaoPendingCount = useMemo(() => {
    return mencaoActivities.filter((a) => a.status === "pending").length;
  }, [mencaoActivities]);

  const revisaoPendingCount = useMemo(() => {
    return revisaoActivities.filter((a) => a.status === "pending").length;
  }, [revisaoActivities]);

  // Handler para mover card no kanban (com optimistic update)
  const handleMoveCard = async (activityId: number, novaEtapa: FazerAmizadeEtapa) => {
    const activity = fazerAmizadeActivities.find((a) => a.id === activityId);
    if (!activity) return;

    const etapaAnterior = activity.etapa_kanban;

    // Se mover para "concluido", abrir modal de completar
    if (novaEtapa === "concluido") {
      setSelectedActivity(activity);
      setIsCompleteModalOpen(true);
      return;
    }

    const novoStatus = novaEtapa === "nao_respondeu" ? "completed" : "pending";
    const queryKey = ["activities", "falar_com_frila", user?.id];

    // Salvar estado anterior para rollback em caso de erro
    const previousData = queryClient.getQueryData<ActivityDetail[]>(queryKey);

    // Optimistic update: atualizar cache imediatamente
    queryClient.setQueryData<ActivityDetail[]>(queryKey, (old) => {
      if (!old) return old;
      return old.map((a) =>
        a.id === activityId
          ? {
              ...a,
              etapa_kanban: novaEtapa,
              status: novoStatus,
              completed_at: novoStatus === "completed" ? new Date().toISOString() : null,
              completed_by: novoStatus === "completed" ? user?.id ?? null : null,
            }
          : a
      );
    });

    // Chamar backend - atualizar atividade
    const { error } = await supabase
      .from("activities")
      .update({
        etapa_kanban: novaEtapa,
        status: novoStatus,
        completed_at: novoStatus === "completed" ? new Date().toISOString() : null,
        completed_by: novoStatus === "completed" ? user?.id : null,
      })
      .eq("id", activityId);

    if (error) {
      // Rollback em caso de erro
      queryClient.setQueryData(queryKey, previousData);
      toast({
        title: "Erro",
        description: "Nao foi possivel mover a atividade.",
        variant: "destructive",
      });
      return;
    }

    // Atualizar falar_com_frila do freelancer
    if (activity.freelancer_id) {
      // Se saiu de "concluido" ou "nao_respondeu" para etapa ativa, remarcar
      const etapasFinais = ["concluido", "nao_respondeu"];
      const etapasAtivas = ["pendente", "em_contato"];
      const saiuDeEtapaFinal = etapasFinais.includes(etapaAnterior || "");
      const foiParaEtapaAtiva = etapasAtivas.includes(novaEtapa);

      if (saiuDeEtapaFinal && foiParaEtapaAtiva) {
        // Remarcar freelancer
        await supabase
          .from("freelancers")
          .update({
            falar_com_frila: true,
            falar_com_frila_marked_at: new Date().toISOString(),
            falar_com_frila_marked_by: user?.id,
          })
          .eq("id", activity.freelancer_id);
      } else if (novaEtapa === "nao_respondeu") {
        // Desmarcar freelancer (nao respondeu tambem é conclusão)
        await supabase
          .from("freelancers")
          .update({
            falar_com_frila: false,
            falar_com_frila_marked_at: null,
            falar_com_frila_marked_by: null,
          })
          .eq("id", activity.freelancer_id);
      }
    }

    // Invalidar contagem de pendentes
    queryClient.invalidateQueries({ queryKey: ["activity_pending_count"] });
  };

  // Handler para click em card do kanban
  const handleKanbanCardClick = (activity: ActivityDetail) => {
    setSelectedActivity(activity);
    setIsCompleteModalOpen(true);
  };

  // Reset subTab quando mudar de tab principal
  const handleMainTabChange = (value: string) => {
    setMainTab(value as MainTab);
    setSubTab("pending");
    setSearchTerm("");
  };

  // --- Reconsultas ---
  const {
    data: reconsultas = [],
    isLoading: isLoadingReconsultas,
    isError: isErrorReconsultas,
    refetch: refetchReconsultas,
  } = useReconsultas();
  const atualizarReconsulta = useAtualizarReconsultaStatus();
  const reconsultasUrgentesCount = useMemo(() => reconsultasUrgentes(reconsultas), [reconsultas]);

  // Arrastar card -> muda o estado da reconsulta conforme a coluna de destino.
  const handleMoveReconsulta = (checkId: string, dest: ReconsultaBucket) => {
    const item = reconsultas.find((reconsulta) => reconsulta.id === checkId);
    if (!item) return;
    if (dest === "feitas" && item.status !== "feito") {
      atualizarReconsulta.mutate({ checkId, status: "feito" });
    } else if (dest === "canceladas" && item.status !== "cancelado") {
      atualizarReconsulta.mutate({ checkId, status: "cancelado" });
    } else if (
      (dest === "atrasadas" || dest === "semana" || dest === "proximas") &&
      item.status !== "pendente"
    ) {
      // Reabrir uma resolvida: volta a pendente (re-bucketiza pela data prevista).
      atualizarReconsulta.mutate({ checkId, status: "pendente" });
    }
  };

  // "Reconsultado" -> abre o dialog de atualizar; ao salvar, resolve a reconsulta.
  const handleReconsultar = (item: ReconsultaItem) => setReconsultaAtiva(item);
  const handleReconsultaSaved = () => {
    if (reconsultaAtiva) {
      atualizarReconsulta.mutate({ checkId: reconsultaAtiva.id, status: "feito" });
    }
  };
  const handleCancelarReconsulta = (checkId: string) => {
    atualizarReconsulta.mutate(
      { checkId, status: "cancelado" },
      { onError: () => toast({ variant: "destructive", title: "Erro ao cancelar reconsulta" }) }
    );
  };

  // Filtrar atividades para listas (Mencoes e Revisao)
  const getFilteredActivities = (activities: ActivityDetail[]) => {
    let filtered = activities;

    // Filtrar por status
    if (subTab === "pending") {
      filtered = filtered.filter((a) => a.status === "pending");
    } else {
      filtered = filtered.filter((a) => a.status === "completed");
    }

    // Filtro de busca
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((activity) => {
        if (activity.tipo === "mencao") {
          return (
            activity.vaga_titulo?.toLowerCase().includes(search) ||
            activity.observacao_mensagem?.toLowerCase().includes(search)
          );
        }
        return (
          activity.freelancer_nome?.toLowerCase().includes(search) ||
          activity.freelancer_email?.toLowerCase().includes(search) ||
          activity.freelancer_cidade?.toLowerCase().includes(search)
        );
      });
    }

    return filtered;
  };

  // Contagens para sub-tabs
  const getMencaoSubTabCounts = () => {
    return {
      pending: mencaoActivities.filter((a) => a.status === "pending").length,
      completed: mencaoActivities.filter((a) => a.status === "completed").length,
    };
  };

  const getRevisaoSubTabCounts = () => {
    return {
      pending: revisaoActivities.filter((a) => a.status === "pending").length,
      completed: revisaoActivities.filter((a) => a.status === "completed").length,
    };
  };

  const mencaoSubTabCounts = getMencaoSubTabCounts();
  const revisaoSubTabCounts = getRevisaoSubTabCounts();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Atividades"
        subtitle="Gerencie suas tarefas e acompanhe o progresso"
      />

      {/* Tabs principais */}
      <Tabs value={mainTab} onValueChange={handleMainTabChange}>
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="falar_com_frila" className="gap-2">
            <HeartHandshake className="h-4 w-4" />
            <span className="hidden sm:inline">Fazer Amizade</span>
            <span className="sm:hidden">Amizade</span>
            {fazerAmizadePendingCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                {fazerAmizadePendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="mencao" className="gap-2">
            <AtSign className="h-4 w-4" />
            Mencoes
            {mencaoPendingCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {mencaoPendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="revisao_cadastro" className="gap-2">
            <ClipboardCheck className="h-4 w-4" />
            <span className="hidden sm:inline">Revisao</span>
            <span className="sm:hidden">Rev.</span>
            {revisaoPendingCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {revisaoPendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reconsultas" className="gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Reconsultas</span>
            <span className="sm:hidden">Recon.</span>
            {reconsultasUrgentesCount > 0 && (
              <Badge variant="destructive" className="ml-1 h-5 px-1.5">
                {reconsultasUrgentesCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Conteudo: Fazer Amizade (Kanban) */}
        <TabsContent value="falar_com_frila" className="mt-6">
          {isLoadingFazerAmizade ? (
            <div className="flex gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="w-72 h-96" />
              ))}
            </div>
          ) : isErrorFazerAmizade ? (
            <ErrorState onRetry={() => refetchFazerAmizade()} />
          ) : (
            <ActivityKanbanBoard
              activities={fazerAmizadeActivities}
              onMoveCard={handleMoveCard}
              onCardClick={handleKanbanCardClick}
            />
          )}
        </TabsContent>

        {/* Conteudo: Mencoes (Lista) */}
        <TabsContent value="mencao" className="space-y-4 mt-6">
          <AtividadesSubTabs
            subTab={subTab}
            setSubTab={setSubTab}
            pendingCount={mencaoSubTabCounts.pending}
            completedCount={mencaoSubTabCounts.completed}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activities={getFilteredActivities(mencaoActivities)}
            isLoading={isLoadingMencao}
            isError={isErrorMencao}
            onRetry={() => refetchMencao()}
            pendingLabel="Pendentes"
            searchPlaceholder="Buscar por vaga..."
            emptyIcon={<AtSign className="h-12 w-12" />}
            emptyTitle="Nenhuma mencao pendente"
            emptyDescription="Quando voce for mencionado em observacoes de vagas, aparecera aqui."
          />
        </TabsContent>

        {/* Conteudo: Revisao de Cadastro (Lista) */}
        <TabsContent value="revisao_cadastro" className="space-y-4 mt-6">
          <AtividadesSubTabs
            subTab={subTab}
            setSubTab={setSubTab}
            pendingCount={revisaoSubTabCounts.pending}
            completedCount={revisaoSubTabCounts.completed}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            activities={getFilteredActivities(revisaoActivities)}
            isLoading={isLoadingRevisao}
            isError={isErrorRevisao}
            onRetry={() => refetchRevisao()}
            pendingLabel="Pendentes"
            searchPlaceholder="Buscar freelancer..."
            emptyIcon={<ClipboardCheck className="h-12 w-12" />}
            emptyTitle="Nenhum cadastro para revisar"
            emptyDescription="Os cadastros pendentes de revisao aparecerao aqui."
          />
        </TabsContent>

        {/* Conteudo: Reconsultas (Kanban) */}
        <TabsContent value="reconsultas" className="mt-6">
          {isLoadingReconsultas ? (
            <div className="flex gap-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="w-72 h-96" />
              ))}
            </div>
          ) : isErrorReconsultas ? (
            <ErrorState onRetry={() => refetchReconsultas()} />
          ) : reconsultas.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto text-muted-foreground/50 mb-4">
                <Bell className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="font-medium text-lg">Nenhuma reconsulta agendada</h3>
              <p className="text-muted-foreground">
                Ao atualizar a disponibilidade de um freelancer, defina "Reconsultar em" para
                agendar um retorno. Ele aparece aqui.
              </p>
            </div>
          ) : (
            <ReconsultaKanbanBoard
              items={reconsultas}
              onMove={handleMoveReconsulta}
              onReconsultar={handleReconsultar}
              onCancelar={handleCancelarReconsulta}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de completar atividade */}
      {selectedActivity && (
        <CompletarAtividadeModal
          isOpen={isCompleteModalOpen}
          onClose={() => {
            setIsCompleteModalOpen(false);
            setSelectedActivity(null);
          }}
          activity={selectedActivity}
        />
      )}

      {/* Dialog de atualizar disponibilidade acionado pelo "Reconsultado". Ao salvar,
          onSaved resolve a reconsulta (status='feito'). */}
      {reconsultaAtiva && (
        <AtualizarDisponibilidadeDialog
          open={!!reconsultaAtiva}
          onOpenChange={(open) => !open && setReconsultaAtiva(null)}
          freelancerId={reconsultaAtiva.freelancer_id}
          freelancerNome={reconsultaAtiva.nome}
          onSaved={handleReconsultaSaved}
        />
      )}
    </div>
  );
}

interface AtividadesSubTabsProps {
  subTab: SubTab;
  setSubTab: (tab: SubTab) => void;
  pendingCount: number;
  completedCount: number;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  activities: ActivityDetail[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  pendingLabel: string;
  searchPlaceholder?: string;
  emptyIcon: React.ReactNode;
  emptyTitle: string;
  emptyDescription: string;
}

function AtividadesSubTabs({
  subTab,
  setSubTab,
  pendingCount,
  completedCount,
  searchTerm,
  setSearchTerm,
  activities,
  isLoading,
  isError,
  onRetry,
  pendingLabel,
  searchPlaceholder = "Buscar freelancer...",
  emptyIcon,
  emptyTitle,
  emptyDescription,
}: AtividadesSubTabsProps) {
  return (
    <>
      {/* Sub-tabs */}
      <Tabs value={subTab} onValueChange={(v) => setSubTab(v as SubTab)}>
        <div className="flex items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              {pendingLabel}
              <Badge variant="secondary" className="ml-1">
                {pendingCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="completed" className="gap-2">
              Concluidas
              <Badge variant="secondary" className="ml-1">
                {completedCount}
              </Badge>
            </TabsTrigger>
          </TabsList>

          {/* Busca */}
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        <TabsContent value="pending" className="mt-4">
          <ActivityList
            activities={activities}
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
            showCompletedInfo={false}
            emptyIcon={emptyIcon}
            emptyTitle={emptyTitle}
            emptyDescription={emptyDescription}
          />
        </TabsContent>

        <TabsContent value="completed" className="mt-4">
          <ActivityList
            activities={activities}
            isLoading={isLoading}
            isError={isError}
            onRetry={onRetry}
            showCompletedInfo={true}
            emptyIcon={emptyIcon}
            emptyTitle="Nenhuma atividade concluida"
            emptyDescription="As atividades concluidas aparecerao aqui."
          />
        </TabsContent>
      </Tabs>
    </>
  );
}

interface ActivityListProps {
  activities: ActivityDetail[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  showCompletedInfo: boolean;
  emptyIcon: React.ReactNode;
  emptyTitle: string;
  emptyDescription: string;
}

function ActivityList({
  activities,
  isLoading,
  isError,
  onRetry,
  showCompletedInfo,
  emptyIcon,
  emptyTitle,
  emptyDescription,
}: ActivityListProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return <ErrorState onRetry={onRetry} />;
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto text-muted-foreground/50 mb-4">{emptyIcon}</div>
        <h3 className="font-medium text-lg">{emptyTitle}</h3>
        <p className="text-muted-foreground">{emptyDescription}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => (
        <ActivityCard
          key={activity.id}
          activity={activity}
          showCompletedInfo={showCompletedInfo}
        />
      ))}
    </div>
  );
}
